import {
  ContentCopy,
  DeleteOutline,
  EditOutlined,
  Search,
  VisibilityOutlined,
} from "@mui/icons-material";
import {
  Alert,
  Card,
  CardContent,
  CardHeader,
  Chip,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useMemo, useState } from "react";
import { PROPOSAL_STATUSES } from "../constants/proposalStatuses";
import { ProposalStatus, SavedProposal } from "../types/proposal";
import { formatCurrency } from "../utils/currency";

type Props = {
  proposals: SavedProposal[];
  activeProposalId: string | null;
  onOpen: (proposal: SavedProposal) => void;
  onView: (proposal: SavedProposal) => void;
  onStatusChange: (proposal: SavedProposal, status: ProposalStatus) => void;
  onCopyNotion: (proposal: SavedProposal) => void;
  onDelete: (proposal: SavedProposal) => void;
};

const formatUpdatedAt = (value: string): string =>
  new Date(value).toLocaleString("pt-BR", {
    dateStyle: "short",
    timeStyle: "short",
  });

const SavedProposalsCard: React.FC<Props> = ({
  proposals,
  activeProposalId,
  onOpen,
  onView,
  onStatusChange,
  onCopyNotion,
  onDelete,
}) => {
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | ProposalStatus>("all");
  const filteredProposals = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return proposals.filter(proposal => {
      const matchesStatus =
        statusFilter === "all" || proposal.status === statusFilter;
      const searchableContent = [
        proposal.id,
        proposal.draft.client.name,
        proposal.draft.client.company,
        proposal.draft.client.email,
      ]
        .join(" ")
        .toLowerCase();
      return matchesStatus && searchableContent.includes(normalizedQuery);
    });
  }, [proposals, query, statusFilter]);

  return (
  <Card>
    <CardHeader
      title="Pipeline comercial"
      subheader={`${filteredProposals.length} de ${proposals.length} proposta(s)`}
    />
    <CardContent sx={{ pt: 0 }}>
      {!proposals.length ? (
        <Alert severity="info">
          Nenhuma proposta foi salva ainda. Preencha o formulário e clique em
          “Salvar proposta”.
        </Alert>
      ) : (
        <>
          <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} sx={{ mb: 2 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Buscar por cliente, empresa, e-mail ou código"
              value={query}
              onChange={event => setQuery(event.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              select
              size="small"
              label="Status"
              value={statusFilter}
              onChange={event =>
                setStatusFilter(event.target.value as "all" | ProposalStatus)
              }
              sx={{ minWidth: { xs: "100%", md: 250 } }}
            >
              <MenuItem value="all">Todos os status</MenuItem>
              {PROPOSAL_STATUSES.map(status => (
                <MenuItem key={status} value={status}>
                  {status}
                </MenuItem>
              ))}
            </TextField>
          </Stack>

          {!filteredProposals.length ? (
            <Alert severity="info">
              Nenhuma proposta corresponde aos filtros informados.
            </Alert>
          ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Proposta</TableCell>
                <TableCell>Cliente</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Total</TableCell>
                <TableCell>Atualizada</TableCell>
                <TableCell align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredProposals.map(proposal => (
                <TableRow key={proposal.id} hover selected={proposal.id === activeProposalId}>
                  <TableCell sx={{ minWidth: 155 }}>
                    <Stack direction="row" alignItems="center" gap={1}>
                      <Typography variant="body2" fontWeight={600}>
                        {proposal.id}
                      </Typography>
                      {proposal.id === activeProposalId && (
                        <Chip label="Aberta" size="small" color="info" />
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ minWidth: 180 }}>
                    <Typography variant="body2">
                      {proposal.draft.client.name || "Sem nome"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {proposal.draft.client.company || "Sem empresa"}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ minWidth: 230 }}>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      value={proposal.status}
                      onChange={event =>
                        onStatusChange(
                          proposal,
                          event.target.value as ProposalStatus,
                        )
                      }
                    >
                      {PROPOSAL_STATUSES.map(status => (
                        <MenuItem key={status} value={status}>
                          {status}
                        </MenuItem>
                      ))}
                    </TextField>
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                    {formatCurrency(proposal.totals.total)}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    {formatUpdatedAt(proposal.updatedAt)}
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                    <Tooltip title="Visualizar e detalhar">
                      <IconButton onClick={() => onView(proposal)}>
                        <VisibilityOutlined />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Abrir para editar">
                      <IconButton onClick={() => onOpen(proposal)}>
                        <EditOutlined />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Copiar anotação para o Notion">
                      <IconButton onClick={() => onCopyNotion(proposal)}>
                        <ContentCopy />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Excluir proposta">
                      <IconButton color="error" onClick={() => onDelete(proposal)}>
                        <DeleteOutline />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
          )}
        </>
      )}
    </CardContent>
  </Card>
  );
};

export default SavedProposalsCard;
